import { CARE_DATES } from '@/constants';
import { CareAttributesPC } from '@/__generated__/globalTypes';
import { JobPostData, OptionalReducerData } from './common';

export type Pets = {
  dogs: number;
  cats: number;
  other: number;
};

export enum ServiceTypes {
  SITTING = 'SITTING',
  BOARDING = 'BOARDING',
  WALKING = 'WALKING',
  GROOMING = 'GROOMING',
  TRAINING = 'TRAINING',
  DAYCARE = 'DAYCARE',
}

export const ServiceTypesLabels: { [key in ServiceTypes]: string } = {
  [ServiceTypes.SITTING]: 'Sitting (at your house)',
  [ServiceTypes.BOARDING]: "Boarding (at caregiver's house)",
  [ServiceTypes.WALKING]: 'Walking',
  [ServiceTypes.GROOMING]: 'Grooming',
  [ServiceTypes.TRAINING]: 'Training',
  [ServiceTypes.DAYCARE]: 'Doggy daycare',
};

export const SERVICE_TYPES_OPTIONS = Object.freeze([
  {
    value: ServiceTypes.SITTING as string,
    label: ServiceTypesLabels[ServiceTypes.SITTING],
  },
  {
    value: ServiceTypes.BOARDING as string,
    label: ServiceTypesLabels[ServiceTypes.BOARDING],
  },
  {
    value: ServiceTypes.WALKING as string,
    label: ServiceTypesLabels[ServiceTypes.WALKING],
  },
  {
    value: ServiceTypes.GROOMING as string,
    label: ServiceTypesLabels[ServiceTypes.GROOMING],
  },
  {
    value: ServiceTypes.TRAINING as string,
    label: ServiceTypesLabels[ServiceTypes.TRAINING],
  },
]);

export const SERVICE_TYPES_OPTIONS_REBRAND = Object.freeze([
  {
    value: ServiceTypes.SITTING as string,
    label: 'Sitting',
    description: 'Caregiver stays with pets at your home',
  },
  {
    value: ServiceTypes.BOARDING as string,
    label: 'Boarding',
    description: "Pets stay at caregiver's home",
  },
  {
    value: ServiceTypes.WALKING as string,
    label: 'Walking',
    description: '30 or 60 min stroll for your pet',
  },
  {
    value: ServiceTypes.GROOMING as string,
    label: 'Grooming',
    description: 'Nail trims, hair brushing, ear cleaning',
  },
  {
    value: ServiceTypes.TRAINING as string,
    label: 'Training',
    description: 'Help teach your pet behavior basics or new skills',
  },
]);

export const CAT_SERVICE_TYPES_OPTIONS = SERVICE_TYPES_OPTIONS_REBRAND.filter((option) =>
  [ServiceTypes.SITTING, ServiceTypes.BOARDING, ServiceTypes.GROOMING].includes(
    option.value as ServiceTypes
  )
);

export type SeekerPCAction =
  | { type: 'setPetsNumber'; pets: Pets; reducer?: OptionalReducerData }
  | { type: 'setServiceType'; serviceType: ServiceTypes; reducer?: OptionalReducerData }
  | { type: 'setPetCareDate'; careDate: CARE_DATES; reducer?: OptionalReducerData }
  | {
      type: 'pc_setSeekerName';
      firstName?: string;
      lastName?: string;
      reducer?: OptionalReducerData;
    }
  | { type: 'pc_setEnrollmentSource'; value: string; reducer?: OptionalReducerData }
  | {
      type: 'setCareAttributesPC';
      careAttributesPC: CareAttributesPC[];
      reducer?: OptionalReducerData;
    };

export const CaregiverAttributesLabels: { [key in CareAttributesPC]: string } = {
  [CareAttributesPC.PET_FIRST_AID]: 'Pet First Aid training',
  [CareAttributesPC.MEDICINE_ADMINISTRATION]: 'Medicine administration experience',
  [CareAttributesPC.CERTIFIED_AND_INSURED]: 'Certified & insured pet sitter',
  [CareAttributesPC.CARE_FOR_MULTIPLE_PETS]: 'Comfortable caring for 2+ pets',
  [CareAttributesPC.PET_TRANSPORTATION]: 'Comfortable transporting pets',
};

export type SeekerPCState = {
  careDate: CARE_DATES;
  pets: Pets;
  additionalServices: [];
  jobPost: JobPostData;
  firstName: string;
  lastName: string;
  serviceType: ServiceTypes | '';
  enrollmentSource: string;
  careAttributesPC: CareAttributesPC[];
};
